import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useAiStatus, useGoals, useWeekReview } from "../lib/queries";
import { formatDay } from "../lib/format";
import { Thinking } from "../components/PlanReview";

/** "Tu semana": how the last seven days went, and a way into the review with the assistant. */
export function Review() {
  const navigate = useNavigate();
  const client = useQueryClient();
  const review = useWeekReview();
  const goals = useGoals();
  const ai = useAiStatus();
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function startReview() {
    setError(null);
    setStarting(true);
    try {
      await api.startReview();
      await client.invalidateQueries({ queryKey: ["chat"] });
      navigate("/asistente");
    } catch (e) {
      setError((e as Error).message);
      setStarting(false);
    }
  }

  if (starting) return <Thinking text="Tu asistente está mirando cómo te fue esta semana…" />;
  if (review.isPending) return <p className="muted center">Cargando…</p>;
  if (!review.data) return <p className="muted center">No pudimos cargar tu semana.</p>;

  const { habits, tasks, from, to } = review.data;
  const scheduled = habits.reduce((n, h) => n + h.scheduled, 0);
  const done = habits.reduce((n, h) => n + h.done, 0);
  const activeGoals = (goals.data ?? []).filter((g) => g.recent.scheduled > 0);

  return (
    <>
      <header className="page-header row start">
        <button className="icon-btn" aria-label="Volver" onClick={() => navigate("/semana")}>‹</button>
        <div>
          <h1>Tu semana</h1>
          <p className="muted small">{formatDay(from)} – {formatDay(to)}</p>
        </div>
      </header>

      <div className="stack">
        <section className="card review-totals">
          <div>
            <span className="review-number">{done}<small> de {scheduled}</small></span>
            <span className="muted small">hábitos hechos</span>
          </div>
          <div>
            <span className="review-number">{tasks.done}<small> de {tasks.total}</small></span>
            <span className="muted small">tareas hechas</span>
          </div>
        </section>

        {habits.length > 0 && (
          <>
            <h2 className="section">Hábitos</h2>
            <ul className="list">
              {habits.map((h) => (
                <li key={h.habitId} className="card review-habit" style={{ borderLeftColor: h.color }}>
                  <div className="grow">
                    <div className="habit-title">{h.emoji} {h.title}</div>
                    <div className="muted small">
                      {h.done} de {h.scheduled}
                      {h.skipped > 0 ? ` · ${h.skipped} “hoy no”` : ""}
                    </div>
                  </div>
                  {h.streak > 1 && <span className="streak" title="Días seguidos">🔥 {h.streak}</span>}
                </li>
              ))}
            </ul>
          </>
        )}

        {activeGoals.length > 0 && (
          <>
            <h2 className="section">Metas, últimas 4 semanas</h2>
            <ul className="list">
              {activeGoals.map((g) => (
                <li key={g.id}>
                  <Link to={`/metas/${g.id}`} className="goal card" style={{ borderLeftColor: g.color }}>
                    <div className="goal-title">{g.title}</div>
                    <RecentProgress done={g.recent.done} scheduled={g.recent.scheduled} />
                    {g.progress.needsReplan && (
                      <div className="small" style={{ color: "var(--primary)", marginTop: 6 }}>
                        ¿Lo hacemos más fácil? Toca para ajustar el plan.
                      </div>
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          </>
        )}

        {ai.data?.enabled && (
          <section className="card stack">
            <p className="summary">
              Tu asistente puede mirar esta semana contigo y proponerte un ajuste pequeño para la próxima.
            </p>
            <button className="btn primary" onClick={() => void startReview()}>Revisar con mi asistente</button>
            {error && <p className="error">{error}</p>}
          </section>
        )}
      </div>
    </>
  );
}

export function RecentProgress({ done, scheduled }: { done: number; scheduled: number }) {
  const percent = Math.round((done / scheduled) * 100);
  return (
    <div className="progress" role="progressbar" aria-valuenow={done} aria-valuemax={scheduled}>
      <div className="progress-track">
        <div className="progress-fill" style={{ width: `${percent}%` }} />
      </div>
      <span>{percent}%</span>
    </div>
  );
}
